import { FC, useMemo } from 'react';
import { Bar } from 'react-chartjs-2';
import clsx from 'clsx';
import type { ChartData, ChartOptions, Plugin } from 'chart.js';
import { CHART_COLORS } from './chartConfig';
import type { HorizontalBarChartProps } from './types';

const VALUE_LABEL_OFFSET_PX = 8;

function createHorizontalBarValueLabels(): Plugin<'bar'> {
  return {
    id: 'horizontalBarValueLabels',
    afterDatasetsDraw(chart) {
      const { ctx, data } = chart;
      const dataset = data.datasets[0];
      if (!dataset?.data) {
        return;
      }
      const meta = chart.getDatasetMeta(0);
      ctx.save();
      ctx.font =
        '500 12px Inter, apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif';
      ctx.fillStyle = CHART_COLORS.labelDark;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';

      meta.data.forEach((element, index) => {
        const raw = dataset.data[index];
        const value = typeof raw === 'number' ? raw : Number(raw);
        if (Number.isNaN(value)) {
          return;
        }
        const formatted = value.toLocaleString();
        const bar = element as unknown as {
          x: number;
          y: number;
          base: number;
        };
        const rightX = Math.max(bar.x, bar.base ?? bar.x);
        ctx.fillText(formatted, rightX + VALUE_LABEL_OFFSET_PX, bar.y);
      });
      ctx.restore();
    },
  };
}

const horizontalBarValueLabelsPlugin = createHorizontalBarValueLabels();

const EMPTY_CATEGORY_LABEL = '\u200b';

const HorizontalBarChart: FC<HorizontalBarChartProps> = ({
  labels,
  datasets,
  height = 200,
  className,
  tooltipTitles,
}) => {
  const primaryDataset = datasets[0];
  const data = useMemo(
    () => primaryDataset?.data ?? [],
    [primaryDataset?.data],
  );
  const color = primaryDataset?.color ?? CHART_COLORS.defaultDark;

  const isEmptyDataset = labels.length === 0 || data.length === 0;

  const displayLabels = useMemo(
    () => (isEmptyDataset ? [EMPTY_CATEGORY_LABEL] : labels),
    [isEmptyDataset, labels],
  );

  const displayData = useMemo(
    () => (isEmptyDataset ? [0] : data),
    [data, isEmptyDataset],
  );

  const barColor = isEmptyDataset ? 'rgba(0,0,0,0)' : color;

  const maxValue = useMemo(() => Math.max(...displayData, 1), [displayData]);

  const chartData = useMemo<ChartData<'bar'>>(
    () => ({
      labels: displayLabels,
      datasets: [
        {
          label: primaryDataset?.label ?? '',
          data: displayData,
          backgroundColor: barColor,
          barThickness: 6,
          borderRadius: 99,
          borderSkipped: false,
        },
      ],
    }),
    [barColor, displayData, displayLabels, primaryDataset?.label],
  );

  const options = useMemo<ChartOptions<'bar'>>(
    () => ({
      indexAxis: 'y',
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: { right: isEmptyDataset ? 8 : 56, left: 0, top: 4, bottom: 4 },
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          enabled: !isEmptyDataset,
          titleFont: { family: 'Inter', size: 12 },
          bodyFont: { family: 'Inter', size: 12 },
          callbacks: {
            title: (items) => {
              const idx = items[0]?.dataIndex;
              if (idx !== undefined && tooltipTitles?.[idx] !== undefined) {
                return tooltipTitles[idx];
              }
              const label = items[0]?.label;
              return typeof label === 'string' ? label : '';
            },
          },
        },
      },
      scales: {
        x: {
          display: false,
          min: 0,
          max: maxValue,
          grid: {
            display: isEmptyDataset,
            color: CHART_COLORS.gridLine,
            drawBorder: false,
          },
        },
        y: {
          grid: {
            display: isEmptyDataset,
            color: CHART_COLORS.gridLine,
            drawBorder: false,
          },
          border: { display: false },
          ticks: {
            display: !isEmptyDataset,
            font: { size: 12, weight: 500 },
            color: CHART_COLORS.labelDark,
            padding: 8,
          },
        },
      },
    }),
    [isEmptyDataset, maxValue, tooltipTitles],
  );

  return (
    <div
      className={clsx('tw-w-full', className)}
      style={{ height: typeof height === 'number' ? `${height}px` : height }}
      data-cy="analytics-chart-horizontal-bar"
    >
      <Bar
        data={chartData}
        options={options}
        plugins={isEmptyDataset ? [] : [horizontalBarValueLabelsPlugin]}
      />
    </div>
  );
};

export default HorizontalBarChart;
